import {
  Alert,
  Box,
  Button,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { levels } from "features/Bookmarks/data/levels";
import useReadingLevel from "features/Bookmarks/hooks/useReadingLevel.hook";
import React, { useEffect, useState } from "react";
import SummaryBox from "../Summarize/Box";

import { EditorState } from "draft-js";
import { markdownToContentState } from "features/Bookmarks/utils/htmltomarkdown";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useSelector } from "react-redux";
import Header from "./Header";
import { useDispatch } from "react-redux";
import { setLevel } from "context/features/dataSlice";
import { useSourceSearch, useTrackedArticles } from "features/SourceSearch";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import ReadingLevelErrorComponent from "features/ReadingLevelComponent/components/reading-level-error";
import { normalizeString } from "utils/string.util";

const ReadingLevel = ({
  levelLoaded,
  setLevelLoaded,
  markdownSummary,
  setmarkdownSummary,
  setDisable,
  selectedBookmarkOption,
  originalSummary,
  setIsAIGeneratedContent,
  isAIGeneratedContent
}) => {
  const [loading, setLoading] = useState(false);

  const { summaryInfoData, readingSummary, isReadingAvailable, level } =
    useSelector((state) => state.data);
  const { hasBeenClicked, updateTrackedLink } = useTrackedArticles(
    summaryInfoData?.url
  );

  const { currentCategory, currentSearchTerm } = useSourceSearch();

  const [editing, setEditing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [rawText, setRawText] = useState("");
  const dispatch = useDispatch();

  const { palette } = useTheme();

  const [htmlSummary, sethtmlSummary] = useState(
    EditorState.createEmpty(markdownToContentState(markdownSummary))
  );

  const navbarBackground = palette?.navbar?.main;

  const {
    getCompositionFromUrl,
    getCompositionFromText,
    changeTooltip,
    handleCopy,
    handleMouseOut,
    error,
    setError,
    errorMessage,
  } = useReadingLevel(
    setmarkdownSummary,
    setLoading,
    markdownSummary,
    sethtmlSummary,
    setDisable,
    setLevelLoaded,
    setIsAIGeneratedContent
  );

  const handleChange = (e) => {
    dispatch(setLevel(e.target.value));
    if (rawText) {
      getCompositionFromText(e.target.value, rawText);
    } else {
      getCompositionFromUrl(e.target.value, summaryInfoData?.url);
    }
  };

  const tooltipTitle = changeTooltip
    ? "Copied to clipboard"
    : "Copy to clipboard";

  const saveSummary = (markdown) => {
    setmarkdownSummary(markdown);
    setIsFocused(false);
  };

  useEffect(() => {
    if (originalSummary && originalSummary.length > 1) {
      if (markdownSummary === originalSummary) {
        setDisable(true);
      } else {
        setDisable(false);
      }
    }
    if (!editing) {
      sethtmlSummary(markdownToContentState(markdownSummary));
    }
  }, [markdownSummary, editing]);

  useEffect(() => {
    if (isReadingAvailable) {
    } else if (
      level &&
      selectedBookmarkOption === "readinglevel" &&
      !levelLoaded
    ) {
      getCompositionFromUrl(level, summaryInfoData?.url);
      setLevelLoaded(true);
    }
  }, [summaryInfoData?.url, selectedBookmarkOption, isReadingAvailable]);

  useEffect(() => {
    if(error) {
      setIsAIGeneratedContent(false);
    }
  }, [error]);

  useEffect(() => {
    updateTrackedLink(summaryInfoData?.url);
  }, [summaryInfoData, updateTrackedLink]);

  if (selectedBookmarkOption !== "readinglevel") return <></>;

  if (!isReadingAvailable && !hasBeenClicked) {
    return (
      <Stack gap={2} p={2} pt={4}>
        <Alert severity="info">
          <Typography>
            It looks like you haven’t opened this article yet. Before using our
            generative AI tools, please open and read the article first. This
            ensures you’re familiar with the original content in case you want
            to edit the AI-generated text. It is also respectful to the human
            writers who worked hard to create thoughtful, credible content for
            you.
          </Typography>
        </Alert>
        <Stack direction="row" justifyContent="center">
          <SearchResultLink
            url={summaryInfoData.url}
            trackingCat="click_resultset_title"
            trackingOptions={{
              date_published: summaryInfoData.utc_date_published,
              url: summaryInfoData.url,
              query:
                currentSearchTerm != null &&
                currentSearchTerm.trim().length > 0
                  ? currentSearchTerm
                  : undefined,
              entity: summaryInfoData.publisher,
              category:
                currentCategory != null && currentCategory.trim().length > 0
                  ? currentCategory
                  : undefined,
              type: "bookmark sidebar",
              title: summaryInfoData.title,
            }}
            component={Button}
            sx={{
              color: palette.primary.contrastText,
            }}
          >
            Open Article
          </SearchResultLink>
        </Stack>
      </Stack>
    );
  }

  if (loading) {
    return (
      <Stack direction="column" gap={2} p={2} pt={4}>
        <Skeleton variant="rectangular" height={50} />
        <Skeleton variant="rectangular" height={150} />
      </Stack>
    );
  }

  return error ? (
    <Box
      sx={{
        px: 2,
      }}
    >
      <ReadingLevelErrorComponent
        errorMessage={errorMessage}
        markdownSummary={markdownSummary}
        setmarkdownSummary={setmarkdownSummary}
        htmlSummary={htmlSummary}
        sethtmlSummary={sethtmlSummary}
        setDisable={setDisable} 
        setLevelLoaded={setLevelLoaded}
        levelLoaded={levelLoaded}
        saveSummary={saveSummary}
        editing={editing}
        setEditing={setEditing}
        isFocused={isFocused}
        setIsFocused={setIsFocused}
      />
    </Box>
  ) : (
    <Stack gap={2} p={2}>
      {(isAIGeneratedContent || !isReadingAvailable) && (
        <React.Fragment>
          <Header />
          <Typography sx={{ mt: 1 }} variant="body2" color="grey.600">
            Generated Content
          </Typography>
        </React.Fragment>
      )}
      <TextField
        onChange={handleChange}
        fullWidth
        select
        label="Select Reading Level"
        value={level}
        InputLabelProps={{
          shrink: Boolean(level),
        }}
      >
        {levels?.map((readlevel) => (
          <MenuItem key={readlevel.level} value={readlevel.level}>
            {readlevel.value}
          </MenuItem>
        ))}
      </TextField>
      <SummaryBox
        editing={editing}
        setEditing={setEditing}
        markdownSummary={markdownSummary}
        htmlSummary={htmlSummary}
        sethtmlSummary={sethtmlSummary}
        saveSummary={saveSummary}
        isFocused={isFocused}
        setIsFocused={setIsFocused}
      />

      {!editing && level && (
        <Stack direction="row" gap={2}>
          <Button
            startIcon={<EditOutlinedIcon />}
            sx={{
              mt: "2rem",
              px: 2,
              py: 1,
              color: "black",
              backgroundColor: navbarBackground,
              textTransform: "none",
            }}
            onClick={() => {
              setEditing((edit) => !edit);
            }}
          >
            Edit
          </Button>

          <Tooltip title={tooltipTitle}>
            <Button
              startIcon={<ContentCopyIcon />}
              sx={{
                mt: "2rem",
                px: 2,
                py: 1,
                color: "black",
                backgroundColor: navbarBackground,
                textTransform: "none",
              }}
              onClick={() => {
                const readinglevel = levels?.filter(
                  (curlevel) => curlevel.level === level
                )[0]?.value;
                handleCopy(readinglevel);
              }}
              onMouseOut={handleMouseOut}
            >
              Copy
            </Button>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );
};

export default ReadingLevel;
